import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LinkButton from "../../../common/LinkButton/LinkButton";
import OnDemandSlotInEditMode from "./OnDemandSlotInEditMode/OnDemandSlotInEditMode";
import { ReactComponent as BulkUpdateInfoIcon } from "../../../assets/svg/BulkUpdateInfoIcon.svg";
import editOnDemandSlotsStyles from "./EditOnDemandSlotsStyles";
import PropTypes from "prop-types";
import { isArrayValid, isEmptyOrNull } from "../../../../utils/DataUtils";

const EditOnDemandSlots = ({
  tabIndex,
  formIndex,
  days,
  generatedSlots,
  handleOnDemandServiceTimeChange,
  handleIndividualSlotFieldsChange,
  updateOnDemandSlot,
  addNewOnDemandSlot,
  handleSaveNewOnDemandSlot,
  configurationBulkUpdatedObj,
  handleBulkUpdateAction,
  resetCurrentConfigurationAfterBulkUpdateRevert,
  loading,
  userInfo,
  makeSlotEditable,
  deleteTemplateSlot,
  cancelEditingASlot,
}) => {
  const classes = editOnDemandSlotsStyles();

  const renderOnDemandSlots = () => {
    const filteredSlots = isArrayValid(generatedSlots)
      ? generatedSlots.filter(
        (slot) =>
          !isEmptyOrNull(slot?.slotAction) && slot.slotAction !== "DELETE"
      )
      : [];

    return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Typography className={classes.slotsHeaderSpanStyle}>First customer order time</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography className={classes.slotsHeaderSpanStyle}> Last order cut-off time</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography className={classes.slotsHeaderSpanStyle}>Capacity</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography className={classes.slotsHeaderSpanStyle}>Actions</Typography>
          </Grid>
        </Grid>

        <div className={classes.slotsContainer}>
          {filteredSlots.map((slot, index) => (
            <OnDemandSlotInEditMode
              key={slot?.id || index}
              userInfo={userInfo}
              tabIndex={tabIndex}
              formIndex={formIndex}
              slotIndex={index}
              slot={slot}
              handleOnDemandServiceTimeChange={handleOnDemandServiceTimeChange}
              handleIndividualSlotFieldsChange={handleIndividualSlotFieldsChange}
              loading={loading}
              canSlotBeDeleted={!!(generatedSlots && generatedSlots.length !== 1)}
              configurationBulkUpdatedObj={configurationBulkUpdatedObj}
              generatedSlots={generatedSlots}
              makeSlotEditable={(slot) => makeSlotEditable(index, slot)}
              saveNewOnDemandSlot={handleSaveNewOnDemandSlot}
              updateOnDemandSlot={updateOnDemandSlot}
              handleDeleteTemplateSlot={(slot) => deleteTemplateSlot(index, slot)}
              cancelEditingASlot={(slot) => cancelEditingASlot(index, slot)}
            />
          ))}
        </div>
      </>
    );
  };

  const renderBulkUpdateMessage = () => {
    const hasGeneratedSlots = isArrayValid(generatedSlots);
    const bulkUpdatedDays = configurationBulkUpdatedObj?.bulkUpdatedDays;
    const hasBulkUpdatedDays = isArrayValid(bulkUpdatedDays);

    if (!hasGeneratedSlots) {
      return null;
    }

    if (!hasBulkUpdatedDays) {
      return (
        <Grid item xs={12} className={classes.bulkUpdateItemGridMain}>
          <BulkUpdateInfoIcon className={classes.bulkUpdateInfoIconStyle} />
          <Typography className={classes.bulkUpdateInfoSpanStyle}>{`You can now duplicate the above configuration for other days as well`}</Typography>
          <Typography className={classes.bulkUpdateLinkSpanStyle} onClick={handleBulkUpdateAction}>{`Click here to bulk update`}</Typography>
        </Grid>
      );
    }

    const firstDay = days?.[0];
    const canRevert = hasBulkUpdatedDays &&
      firstDay &&
      bulkUpdatedDays.includes(firstDay) &&
      resetCurrentConfigurationAfterBulkUpdateRevert !== undefined;

    if (canRevert) {
      return (
        <Grid item xs={12} className={classes.bulkUpdateRevertItemGridMain}>
          <Typography className={classes.bulkUpdateRevertSpanStyle}>{`Configuration for this days is changed in bulk. Want to revert back ?`}</Typography>
          <Typography className={classes.bulkUpdateRevertLinkSpanStyle} onClick={() => resetCurrentConfigurationAfterBulkUpdateRevert(days[0])}>{`Click here to revert back`}</Typography>
        </Grid>
      );
    }

    return null;
  }

  return (
    <Accordion defaultExpanded={false} className={classes.whiteBackground}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.slotsAccordion}>
        <Grid container>
          <Grid item xs={6}>
            <div className={classes.dayDivStyle}>
              <Typography className={classes.slotforSpanStyle}>{`Slots for : `} </Typography>
              <Typography className={classes.dayStyles}>{days[0].toLowerCase()}</Typography>
            </div>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={classes.generatedSlotsAccordionDetails}>
        <Grid item xs={12} className={classes.generatedSlotsInfoGridItem}>
          {isArrayValid(generatedSlots) ? renderOnDemandSlots() : null}
        </Grid>

        <Grid item xs={12} className={classes.generatedSlotsInfoGridItem}>
          <LinkButton buttonLabel="Add New Slot" onClick={() => addNewOnDemandSlot(tabIndex, formIndex, days)} />
        </Grid>

        {renderBulkUpdateMessage()}
      </AccordionDetails>
    </Accordion>
  );
};

EditOnDemandSlots.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  formIndex: PropTypes.number.isRequired,
  days: PropTypes.array.isRequired,
  generatedSlots: PropTypes.array,
  handleOnDemandServiceTimeChange: PropTypes.func.isRequired,
  handleIndividualSlotFieldsChange: PropTypes.func.isRequired,
  updateOnDemandSlot: PropTypes.func.isRequired,
  addNewOnDemandSlot: PropTypes.func.isRequired,
  handleSaveNewOnDemandSlot: PropTypes.func.isRequired,
  configurationBulkUpdatedObj: PropTypes.object,
  handleBulkUpdateAction: PropTypes.func,
  resetCurrentConfigurationAfterBulkUpdateRevert: PropTypes.func,
  loading: PropTypes.bool,
  userInfo: PropTypes.object,
  makeSlotEditable: PropTypes.func.isRequired,
  deleteTemplateSlot: PropTypes.func.isRequired,
  cancelEditingASlot: PropTypes.func.isRequired,
};

export default React.memo(EditOnDemandSlots);
